/**
 * Serial Number Service
 *
 * Captures serial numbers / barcodes during provisioning (AT_OFFICE stage)
 * and propagates them across all related entities for warranty, support,
 * and fault tracing.
 *
 * Cross-entity propagation:
 *   provisioning_items → serial_numbers (index collection)
 *                      → jobs (plannedMaterials / provisioningItems)
 *                      → deals (provisioningSerials map)
 *                      → customers (equipmentSerials array)
 *                      → faults (if fault-sourced)
 */

import {
  collection,
  doc,
  setDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  arrayUnion,
  runTransaction,
  writeBatch,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import type { SerialNumberRecord } from '../types/theo-workflow.types';
import { getTenantIdSync, tenantIdReadUnion } from '../hooks/useTenantId';

const SERIAL_COLLECTION = 'serial_numbers';

/**
 * Build a deterministic Firestore doc ID for the serial_numbers index
 * from (provisioningItemId, serialNumber). SHA-256 over `itemId|serial`
 * gives a stable 64-char hex ID. Running captureSerialNumber twice for
 * the same item+serial (retry after a network blip, offline replay)
 * writes to the same doc instead of creating a duplicate index record.
 * (AUDIT_NOTES M8 / FIX_PLAN §5)
 */
async function buildDeterministicSerialId(
  itemId: string,
  serialNumber: string,
): Promise<string> {
  const key = `${itemId}|${serialNumber.trim()}`;
  const data = new TextEncoder().encode(key);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Capture a serial number for a provisioning item and propagate to all related entities.
 *
 * 1. Writes serialNumber to the provisioning_items document
 * 2. Creates a serial_numbers index record with cross-entity references
 * 3. Propagates serial to deal, customer, linked jobs, and fault (fire-and-forget)
 *
 * @param itemId - Provisioning item Firestore doc ID
 * @param serialNumber - Scanned barcode or manually entered serial
 * @param userId - ID of the user capturing the serial
 * @param userName - Display name of the user
 */
export async function captureSerialNumber(
  itemId: string,
  serialNumber: string,
  userId: string,
  userName: string
): Promise<string> {
  const now = new Date().toISOString();
  const tenantId = getTenantIdSync();

  // 1. Update provisioning item with serial number
  const itemRef = doc(db, 'provisioning_items', itemId);
  await updateDoc(itemRef, {
    serialNumber,
    serialNumbers: [serialNumber],
    serialCapturedAt: now,
    serialCapturedBy: userId,
    updatedAt: Timestamp.now(),
  });

  // 2. Read the item to get dealId, faultId, itemName for the index record
  const itemSnap = await getDoc(itemRef);
  if (!itemSnap.exists()) {
    throw new Error(`Provisioning item ${itemId} not found`);
  }
  const itemData = itemSnap.data();
  const dealId = itemData.dealId || undefined;
  const faultId = itemData.faultId || undefined;
  const itemName = itemData.itemName || 'Unknown item';
  const supplier = itemData.supplier || undefined;

  // 3. Resolve related entities (customer + linked jobs) from the deal
  //    or, for fault-sourced provisioning items, from the fault.
  let customerId: string | undefined;
  let customerName: string | undefined;
  let jobIds: string[] = [];

  if (dealId) {
    try {
      const dealSnap = await getDoc(doc(db, 'deals', dealId));
      if (dealSnap.exists()) {
        const deal = dealSnap.data();
        customerId = deal.customerId || undefined;
        customerName = deal.customerName || undefined;
        // Collect linked install job IDs
        if (deal.installJobIds) {
          jobIds = deal.installJobIds;
        } else if (deal.installJobId) {
          jobIds = [deal.installJobId];
        }
        // Also check createdJobId (pre-site jobs)
        if (deal.createdJobId && !jobIds.includes(deal.createdJobId)) {
          jobIds.push(deal.createdJobId);
        }
      }
    } catch (err) {
      console.error('[SerialNumber] Failed to resolve deal:', err);
    }
  } else if (faultId) {
    // Fault-sourced provisioning items (no deal link). Pull customerId from
    // the fault so equipment still lands on the customer's equipment list.
    // (FIX_PLAN HP8)
    try {
      const faultSnap = await getDoc(doc(db, 'faults', faultId));
      if (faultSnap.exists()) {
        const fault = faultSnap.data();
        customerId = fault.customerId || undefined;
        customerName = fault.clientName || undefined;
      }
    } catch (err) {
      console.error('[SerialNumber] Failed to resolve fault:', err);
    }
  }

  // 4. Create the serial_numbers index record
  const record: Omit<SerialNumberRecord, 'id'> = {
    serialNumber,
    itemName,
    provisioningItemId: itemId,
    capturedAt: now,
    capturedBy: userId,
    capturedByName: userName,
    tenantId,
    ...(dealId ? { dealId } : {}),
    ...(customerId ? { customerId } : {}),
    ...(customerName ? { customerName } : {}),
    ...(jobIds.length > 0 ? { jobIds } : {}),
    ...(faultId ? { faultId } : {}),
    ...(supplier ? { supplier } : {}),
  };

  // setDoc with a deterministic ID makes this write idempotent — retries
  // or offline replays won't create duplicate index records. (AUDIT_NOTES M8)
  const serialDocId = await buildDeterministicSerialId(itemId, serialNumber);
  const serialDocRef = doc(db, SERIAL_COLLECTION, serialDocId);
  await setDoc(serialDocRef, record);

  // Newly-captured serial should be searchable immediately.
  invalidateSerialSearchCache();

  // 5. Fire-and-forget: propagate serial to related entities
  propagateSerialToEntities(serialNumber, itemName, {
    dealId,
    customerId,
    jobIds,
    faultId,
    provisioningItemId: itemId,
  }).catch((err) => {
    console.error('[SerialNumber] Propagation failed (non-blocking):', err);
  });

  console.log(
    `[SerialNumber] Captured ${serialNumber} for "${itemName}" (item: ${itemId}, deal: ${dealId || 'n/a'})`
  );
  return serialDocRef.id;
}

/**
 * Capture MULTIPLE serial numbers for a provisioning item with quantity > 1.
 *
 * For each serial:
 * 1. Updates the provisioning_items document with the array of serials
 * 2. Creates one serial_numbers index record per serial
 * 3. Propagates each serial to deal, customer, linked jobs, fault (fire-and-forget)
 *
 * The legacy single-serial `serialNumber` field is set to the first serial in
 * the array for backward compatibility with code that reads it directly.
 *
 * @param itemId - Provisioning item Firestore doc ID
 * @param serialNumbers - Array of scanned barcodes / manually entered serials
 * @param userId - ID of the user capturing the serials
 * @param userName - Display name of the user
 * @returns Array of created serial_numbers index doc IDs
 */
export async function captureMultipleSerialNumbers(
  itemId: string,
  serialNumbers: string[],
  userId: string,
  userName: string,
): Promise<string[]> {
  const cleaned = serialNumbers.map((s) => s.trim()).filter((s) => s.length > 0);
  if (cleaned.length === 0) return [];

  // Single serial — delegate to the existing single-capture path so the
  // serialNumbers array still gets written.
  if (cleaned.length === 1) {
    const id = await captureSerialNumber(itemId, cleaned[0], userId, userName);
    return [id];
  }

  const now = new Date().toISOString();
  const tenantId = getTenantIdSync();

  // 1. Update provisioning item with serial numbers array (and legacy single field)
  const itemRef = doc(db, 'provisioning_items', itemId);
  await updateDoc(itemRef, {
    serialNumber: cleaned[0], // legacy single-serial field — first serial
    serialNumbers: cleaned,
    serialCapturedAt: now,
    serialCapturedBy: userId,
    updatedAt: Timestamp.now(),
  });

  // 2. Read item to get cross-entity references
  const itemSnap = await getDoc(itemRef);
  if (!itemSnap.exists()) {
    throw new Error(`Provisioning item ${itemId} not found`);
  }
  const itemData = itemSnap.data();
  const dealId = itemData.dealId || undefined;
  const faultId = itemData.faultId || undefined;
  const itemName = itemData.itemName || 'Unknown item';
  const supplier = itemData.supplier || undefined;

  // 3. Resolve customer + jobs (same as single capture path)
  let customerId: string | undefined;
  let customerName: string | undefined;
  let jobIds: string[] = [];

  if (dealId) {
    try {
      const dealSnap = await getDoc(doc(db, 'deals', dealId));
      if (dealSnap.exists()) {
        const deal = dealSnap.data();
        customerId = deal.customerId || undefined;
        customerName = deal.customerName || undefined;
        if (deal.installJobIds) {
          jobIds = deal.installJobIds;
        } else if (deal.installJobId) {
          jobIds = [deal.installJobId];
        }
        if (deal.createdJobId && !jobIds.includes(deal.createdJobId)) {
          jobIds.push(deal.createdJobId);
        }
      }
    } catch (err) {
      console.error('[SerialNumber] Failed to resolve deal:', err);
    }
  } else if (faultId) {
    try {
      const faultSnap = await getDoc(doc(db, 'faults', faultId));
      if (faultSnap.exists()) {
        const fault = faultSnap.data();
        customerId = fault.customerId || undefined;
        customerName = fault.clientName || undefined;
      }
    } catch (err) {
      console.error('[SerialNumber] Failed to resolve fault:', err);
    }
  }

  // 4. Create one serial_numbers index doc per serial — idempotent via
  //    deterministic IDs so retries don't create duplicates.
  const docIds: string[] = [];
  for (const serialNumber of cleaned) {
    const record: Omit<SerialNumberRecord, 'id'> = {
      serialNumber,
      itemName,
      provisioningItemId: itemId,
      capturedAt: now,
      capturedBy: userId,
      capturedByName: userName,
      tenantId,
      ...(dealId ? { dealId } : {}),
      ...(customerId ? { customerId } : {}),
      ...(customerName ? { customerName } : {}),
      ...(jobIds.length > 0 ? { jobIds } : {}),
      ...(faultId ? { faultId } : {}),
      ...(supplier ? { supplier } : {}),
    };

    const serialDocId = await buildDeterministicSerialId(itemId, serialNumber);
    const serialDocRef = doc(db, SERIAL_COLLECTION, serialDocId);
    await setDoc(serialDocRef, record);
    docIds.push(serialDocRef.id);

    // Fire-and-forget propagation per serial — independent failures
    // shouldn't block the rest.
    propagateSerialToEntities(serialNumber, itemName, {
      dealId,
      customerId,
      jobIds,
      faultId,
      provisioningItemId: itemId,
    }).catch((err) => {
      console.error(
        `[SerialNumber] Propagation failed for ${serialNumber} (non-blocking):`,
        err,
      );
    });
  }

  // Newly-captured serials should be searchable immediately.
  invalidateSerialSearchCache();

  console.log(
    `[SerialNumber] Captured ${cleaned.length} serials for "${itemName}" (item: ${itemId}, deal: ${dealId || 'n/a'})`,
  );
  return docIds;
}

/**
 * Propagate a serial number to related entities (deal, customer, jobs, fault).
 * Runs fire-and-forget — failures are logged but never block the capture flow.
 */
async function propagateSerialToEntities(
  serialNumber: string,
  itemName: string,
  refs: {
    dealId?: string;
    customerId?: string;
    jobIds?: string[];
    faultId?: string;
    provisioningItemId: string;
  }
): Promise<void> {
  // Normalise itemName (trim + lowercase) before building the arrayUnion
  // entry. Firestore arrayUnion dedups by deep equality — case or
  // whitespace drift in itemName would cause duplicate entries in
  // provisioningSerials / equipmentSerials arrays over time.
  // (AUDIT_NOTES M9 / FIX_PLAN §5)
  const normalizedItemName = itemName.trim().toLowerCase();
  const entry = {
    serialNumber,
    itemName: normalizedItemName,
    provisioningItemId: refs.provisioningItemId,
  };

  // Deal / Customer / Fault — three arrayUnion writes batched so the serial
  // either lands on every applicable entity or none of them. Pre-fix these
  // were three independent updateDoc calls with per-entity try/catch swallow,
  // which produced split-brain (serial visible on the deal but missing from
  // the customer, etc.) on partial failure. Tracked as H9.
  // Note: the per-job loop below stays in its own runTransaction because it
  // does a read-modify-write on plannedMaterials, which a batch can't express.
  const batch = writeBatch(db);
  let willCommit = false;
  const targets: string[] = [];
  const propagatedAt = new Date().toISOString();

  if (refs.dealId) {
    batch.update(doc(db, 'deals', refs.dealId), {
      provisioningSerials: arrayUnion(entry),
      updatedAt: propagatedAt,
    });
    targets.push('deal');
    willCommit = true;
  }

  if (refs.customerId) {
    batch.update(doc(db, 'customers', refs.customerId), {
      equipmentSerials: arrayUnion(entry),
      updatedAt: propagatedAt,
    });
    targets.push('customer');
    willCommit = true;
  }

  if (refs.faultId) {
    batch.update(doc(db, 'faults', refs.faultId), {
      equipmentSerials: arrayUnion(entry),
      updatedAt: propagatedAt,
    });
    targets.push('fault');
    willCommit = true;
  }

  if (willCommit) {
    try {
      await batch.commit();
    } catch (err) {
      console.error(
        `[SerialNumber] Failed to propagate serial to ${targets.join('/')} (batch failed atomically — none updated):`,
        err,
      );
    }
  }

  // Jobs — update matching plannedMaterials entry with serial.
  // Wrapped in a transaction so concurrent serial captures on the same job
  // (e.g. two techs scanning gear simultaneously) don't overwrite each
  // other's serials via stale reads. (FIX_PLAN B4)
  if (refs.jobIds && refs.jobIds.length > 0) {
    for (const jobId of refs.jobIds) {
      try {
        await runTransaction(db, async (tx) => {
          const jobRef = doc(db, 'jobs', jobId);
          const jobSnap = await tx.get(jobRef);
          if (!jobSnap.exists()) return;
          const materials = jobSnap.data().plannedMaterials || [];
          let updated = false;
          for (const mat of materials) {
            // Match by provisioning item ID only. Name-based fallback was
            // removed because two different items with the same name (e.g.
            // two "U7-Pro AP" rows) would collide and the wrong row would
            // get the serial. IDs are stable and unambiguous.
            // (AUDIT_NOTES L6 / FIX_PLAN §5)
            if (mat.id === refs.provisioningItemId) {
              mat.serialNumber = serialNumber;
              updated = true;
              break;
            }
          }
          if (updated) {
            tx.update(jobRef, {
              plannedMaterials: materials,
              updatedAt: new Date().toISOString(),
            });
          }
        });
      } catch (err) {
        console.error(`[SerialNumber] Failed to propagate to job ${jobId}:`, err);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Serial-search session cache
// ---------------------------------------------------------------------------
// Firestore can't do LIKE/substring queries, so serial search fetches the whole
// tenant serial_numbers index and filters client-side. Global search fires this
// for ANY contiguous ≥6-digit query (serials AND bare phone numbers), so an
// uncached read-per-search was a full-collection scan on every numeric lookup.
// We cache the fetched records per tenant for a short TTL (mirrors
// useAllContacts) — turning N reads/session into ~1 — and invalidate the cache
// whenever a new serial is captured so freshly-scanned gear is searchable.

let serialCache: { tenantId: string; records: SerialNumberRecord[]; at: number } | null = null;
let serialInflight: Promise<SerialNumberRecord[]> | null = null;
const SERIAL_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/** Forget the cached serial index (called after a capture writes a new serial). */
export function invalidateSerialSearchCache(): void {
  serialCache = null;
  serialInflight = null;
}

async function loadAllSerials(tid: string): Promise<SerialNumberRecord[]> {
  const q = query(
    collection(db, SERIAL_COLLECTION),
    where('tenantId', 'in', tenantIdReadUnion(tid)),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as SerialNumberRecord);
}

/**
 * Search serial numbers across all entities.
 * Reads the serial_numbers index once per tenant per session (cached), then
 * substring-filters in memory.
 *
 * @param searchTerm - Full or partial serial number to search
 * @param tenantId - Tenant ID for data isolation
 * @returns Matching serial number records
 */
export async function searchSerialNumbers(
  searchTerm: string,
  tenantId?: string
): Promise<SerialNumberRecord[]> {
  const tid = tenantId || getTenantIdSync();
  const fresh =
    serialCache &&
    serialCache.tenantId === tid &&
    Date.now() - serialCache.at < SERIAL_CACHE_TTL_MS;

  if (!fresh) {
    // Union-read so legacy-alias docs surface during migration.
    if (!serialInflight) {
      serialInflight = loadAllSerials(tid)
        .then((records) => {
          serialCache = { tenantId: tid, records, at: Date.now() };
          serialInflight = null;
          return records;
        })
        .catch((err) => {
          serialInflight = null;
          throw err;
        });
    }
    await serialInflight;
  }

  const records = serialCache?.records ?? [];
  const term = searchTerm.toLowerCase();
  return records.filter(
    (r) =>
      (r.serialNumber || '').toLowerCase().includes(term) ||
      (r.itemName || '').toLowerCase().includes(term) ||
      (r.customerName || '').toLowerCase().includes(term) ||
      (r.supplier || '').toLowerCase().includes(term)
  );
}

/**
 * Get all serial numbers for a specific deal.
 */
export async function getSerialsByDeal(dealId: string): Promise<SerialNumberRecord[]> {
  const q = query(
    collection(db, SERIAL_COLLECTION),
    where('dealId', '==', dealId)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as SerialNumberRecord));
}

/**
 * Get all serial numbers for a specific customer.
 */
export async function getSerialsByCustomer(customerId: string): Promise<SerialNumberRecord[]> {
  const q = query(
    collection(db, SERIAL_COLLECTION),
    where('customerId', '==', customerId)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as SerialNumberRecord));
}
